import React, { useEffect, useRef, useState } from 'react';
import Button from 'components/Button/Button';
import Modal, { ModalRef } from 'components/Modal/Modal';
import DownloadIcon from 'assets/icons/download.svg?react';
import AGSimpleSelect, { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import LinesIcon from 'assets/icons/lines.svg?react';
import { SpotInterface } from 'types';
import { DateRangePicker, formatISODate } from 'shared-ui';
import styles from './IncomeStatement.module.css';
import { downloadClient } from 'clients';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { HTTPError } from 'clients/HTTPClient';
import { trackEvent } from 'utils/analytics';
import { getOrgLaunchDate, INCOME_STATEMENT_PRESETS } from './utils';

type DownloadStatementProps = {
    propertyOptions: SelectOption[] | undefined;
    startDate: string;
    endDate: string;
    spotPk: string;
    orgSpots: Record<string, SpotInterface>;
    orgUuid: string;
};

const DownloadStatement = ({ propertyOptions, orgSpots, orgUuid, ...props }: DownloadStatementProps) => {
    const [spotPk, setSpotPk] = useState(props.spotPk);
    const [startDate, setStartDate] = useState(props.startDate);
    const [endDate, setEndDate] = useState(props.endDate);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const today = new Date();
    const launchDate = getOrgLaunchDate(orgSpots);

    const downloadModalRef = useRef<ModalRef>();

    useEffect(() => {
        setSpotPk(props.spotPk);
    }, [props.spotPk]);

    useEffect(() => {
        setStartDate(props.startDate);
    }, [props.startDate]);

    useEffect(() => {
        setEndDate(props.endDate);
    }, [props.endDate]);

    async function downloadStatement() {
        try {
            setLoading(true);
            const payload = { orgUuid, startDate, endDate, spotUuid: spotPk ? orgSpots[spotPk].uuid : '' };
            trackEvent('Download income statement', payload);
            await downloadClient.downloadIncomeStatementReport(payload);
            downloadModalRef.current?.closeModal();
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage('Something went wrong');
            }
        }
        setLoading(false);
    }

    return (
        <div>
            <Button size="sm" onClick={() => downloadModalRef?.current?.openModal()} className={styles.downloadButton}>
                <DownloadIcon />
                Download
            </Button>

            <Modal ref={downloadModalRef} title="Download Income Statement" align="top">
                <div className={styles.downloadModalContainer}>
                    <p>Get a CSV file containing Revenue, Refunds, Taxes, Expenses, and Net Income.</p>
                    {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
                    <div>
                        <p>Date Range</p>
                        <DateRangePicker
                            format="date"
                            fromValue={startDate}
                            toValue={endDate}
                            onFromValueChange={(value) => setStartDate(formatISODate(value) ?? '')}
                            onToValueChange={(value) => setEndDate(formatISODate(value) ?? '')}
                            formatDateConfig={{ compact: true, timeZoneDisplay: false }}
                            containerProps={{ style: { width: '100%' } }}
                            presets={INCOME_STATEMENT_PRESETS}
                            presetsOrientation={'left'}
                            className={styles.datePicker}
                            disabledDates={launchDate ? [{ before: launchDate }, { after: today }] : { after: today }}
                            startMonth={launchDate}
                            endMonth={today}
                        />
                    </div>
                    {!!propertyOptions && (
                        <div>
                            <p>Properties</p>
                            <AGSimpleSelect
                                icon={<LinesIcon />}
                                width="100%"
                                selected={spotPk}
                                options={propertyOptions}
                                onSelect={(v) => setSpotPk(v)}
                                isInModal
                            />
                        </div>
                    )}

                    <Button
                        size="sm"
                        className={styles.modalDownloadButton}
                        onClick={downloadStatement}
                        loading={loading}
                    >
                        <DownloadIcon /> Download
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default DownloadStatement;
